import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Baby, Banknote, Calendar, Car, CheckCircle2, Clock,
  CreditCard, Loader2, Luggage, MapPin, Plane, ShieldCheck, User, Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { AddressInput } from "../components/AddressInput";
import { Footer } from "../components/Footer";
import { Logo } from "../components/Logo";
import { useLang } from "../i18n";

const today = () => new Date().toISOString().split("T")[0];

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-navy outline-none transition-colors focus:border-azure focus:ring-2 focus:ring-azure/15";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/50";

export default function Booking() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t, lang } = useLang();
  const devisMode = params.get("devis") === "1";

  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [devisSent, setDevisSent] = useState(false);
  const [locations, setLocations] = useState([]);

  const [form, setForm] = useState({
    date: params.get("date") || today(),
    time: params.get("time") || "12:00",
    passengers: 2, luggage: 2, flightNumber: "", childSeat: false, childSeatAge: "",
    vehicle: "berline",
    service: params.get("service") === "hourly" ? "hourly" : "transfer",
    hours: 3,
    pickupAddress: "", dropoffAddress: "",
    name: "", email: "", phone: "", paymentMethod: "onboard_card",
    notes: { congres: t("booking.devis_subject_congres"), mariage: t("booking.devis_subject_mariage") }[params.get("sujet")] || "",
  });
  const [pickupPlace, setPickupPlace] = useState(null); // {label, lat, lon, city, exact}
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [custom, setCustom] = useState({ pickup: "", dropoff: "" });

  const hourlyMode = form.service === "hourly" && !devisMode;
  const customMode = devisMode;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toGeo = (p) => p && { label: p.label, lat: p.lat, lon: p.lon, city: p.city || "" };

  // Préremplissage : widget d'accueil (sessionStorage) puis liens ?from=&to= (lieux connus)
  useEffect(() => {
    let pre = null;
    try {
      pre = JSON.parse(sessionStorage.getItem("cabnow_prefill") || "null");
      if (pre && (pre.pickup || pre.dropoff)) sessionStorage.removeItem("cabnow_prefill");
    } catch { pre = null; }
    if (pre && (pre.pickup || pre.dropoff)) {
      if (pre.pickup) setPickupPlace(pre.pickup);
      if (pre.dropoff) setDropoffPlace(pre.dropoff);
      setForm((f) => ({
        ...f,
        pickupAddress: pre.pickup?.label || f.pickupAddress,
        dropoffAddress: pre.dropoff?.label || f.dropoffAddress,
        date: pre.date || f.date, time: pre.time || f.time,
      }));
    }
    api.get("/locations").then((r) => {
      setLocations(r.data);
      if (pre && (pre.pickup || pre.dropoff)) return;
      const fl = r.data.find((l) => l.id === params.get("from"));
      const tl = r.data.find((l) => l.id === params.get("to"));
      const toPlace = (l) => ({
        label: l.label, lat: l.lat, lon: l.lng, city: l.label,
        exact: l.kind === "airport" || l.kind === "station",
      });
      if (fl) { setPickupPlace(toPlace(fl)); setForm((f) => ({ ...f, pickupAddress: fl.label })); }
      if (tl) { setDropoffPlace(toPlace(tl)); setForm((f) => ({ ...f, dropoffAddress: tl.label })); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prix calculé automatiquement dès que les adresses sont choisies dans les suggestions
  useEffect(() => {
    if (devisMode) return undefined;
    const ready = hourlyMode ? pickupPlace : (pickupPlace && dropoffPlace);
    if (!ready) { setQuote(null); return undefined; }
    let cancelled = false;
    setLoadingQuote(true);
    api.post("/quote", {
      pickup: toGeo(pickupPlace),
      dropoff: hourlyMode ? toGeo(pickupPlace) : toGeo(dropoffPlace),
      ...(hourlyMode ? { hours: form.hours } : {}),
    }).then((r) => { if (!cancelled) setQuote(r.data); })
      .catch((e) => { if (!cancelled) { setQuote(null); toast.error(formatApiError(e)); } })
      .finally(() => { if (!cancelled) setLoadingQuote(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupPlace, dropoffPlace, form.hours, hourlyMode, devisMode]);

  const pickupKind = quote?.pickup_place?.kind;
  const refRequired = !customMode && (pickupKind === "airport" || pickupKind === "station");
  const exactOk = (place, kind) =>
    place && (place.exact || kind === "airport" || kind === "station");

  const pickupLabel = customMode ? custom.pickup || "—" : pickupPlace?.label || "—";
  const dropoffLabel = customMode ? custom.dropoff || "—" : dropoffPlace?.label || "—";

  function tArr(key) {
    const v = t(key);
    return Array.isArray(v) ? v : [v];
  }
  const stepLabels = tArr(customMode ? "booking.steps_devis" : "booking.steps");

  const validateContact = () => {
    if (form.name.trim().length < 2) { toast.error(t("booking.err_name")); return false; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error(t("booking.err_email")); return false; }
    if (form.phone.trim().length < 6) { toast.error(t("booking.err_phone")); return false; }
    return true;
  };

  const nextFromItinerary = () => {
    if (!form.date || !form.time) return toast.error(t("booking.err_datetime"));
    if (customMode) {
      if (custom.pickup.trim().length < 3 || custom.dropoff.trim().length < 3)
        return toast.error(t("booking.err_addresses"));
      setStep(1);
      return undefined;
    }
    if (!pickupPlace || (!hourlyMode && !dropoffPlace))
      return toast.error(t("booking.err_select_address"));
    if (!hourlyMode && pickupPlace.label === dropoffPlace.label)
      return toast.error(t("booking.err_locations"));
    if (!exactOk(pickupPlace, pickupKind) || (!hourlyMode && !exactOk(dropoffPlace, quote?.dropoff_place?.kind)))
      return toast.error(t("booking.err_exact_address"));
    if (refRequired && !form.flightNumber.trim())
      return toast.error(t(pickupKind === "airport" ? "booking.err_flight" : "booking.err_train"));
    if (!quote) return toast.error(t("booking.err_select_address"));
    if (form.childSeat && !form.childSeatAge)
      return toast.error(t("booking.err_child_seat_age"));
    setStep(1);
    return undefined;
  };

  const submitBooking = async () => {
    if (!validateContact()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        pickup: toGeo(pickupPlace),
        dropoff: hourlyMode ? toGeo(pickupPlace) : toGeo(dropoffPlace),
        service_type: hourlyMode ? "hourly" : "transfer", hours: form.hours,
        date: form.date, time: form.time,
        passengers: form.passengers, luggage: form.luggage, vehicle: form.vehicle,
        flight_number: form.flightNumber, child_seat: form.childSeat, child_seat_age: form.childSeatAge,
        name: form.name, email: form.email, phone: form.phone, notes: form.notes,
        payment_method: form.paymentMethod, lang, origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        navigate(`/confirmation/${data.booking_id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  const submitDevis = async () => {
    if (!validateContact()) return;
    setSubmitting(true);
    try {
      await api.post("/quote-requests", {
        pickup_address: custom.pickup, dropoff_address: custom.dropoff,
        date: form.date, time: form.time, passengers: form.passengers,
        name: form.name, email: form.email, phone: form.phone, notes: form.notes,
        lang, origin_url: window.location.origin,
      });
      setDevisSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const PAY_OPTIONS = [
    { id: "online", icon: CreditCard, title: t("booking.online_title"), text: t("booking.online_text") },
    { id: "onboard_card", icon: Car, title: t("booking.onboard_card_title"), text: t("booking.onboard_card_text") },
    { id: "onboard_cash", icon: Banknote, title: t("booking.onboard_cash_title"), text: t("booking.onboard_cash_text") },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-sand" data-testid="booking-page">
      <Navbar />

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-24 pt-32 sm:px-6">
        <Link to="/" data-testid="booking-back-home" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-navy/60 transition-colors hover:text-azure">
          <ArrowLeft size={15} /> {t("common.back_home")}
        </Link>

        {devisSent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl border border-slate-100 bg-white p-12 text-center shadow-xl"
            data-testid="devis-success"
          >
            <CheckCircle2 size={52} className="mx-auto text-emerald-500" />
            <h1 className="mt-6 font-serif text-3xl font-semibold text-navy">{t("booking.devis_title")}</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-navy/60">
              {t("booking.devis_text", { name: form.name.split(" ")[0], from: custom.pickup, to: custom.dropoff })}
            </p>
            <Link to="/" className="mt-8 inline-block rounded-full bg-azure px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover">
              {t("common.back_home")}
            </Link>
          </motion.div>
        ) : (
          <div className="rounded-3xl border border-slate-100 bg-white shadow-xl" data-testid="booking-card">
            {/* Stepper */}
            <div className="flex items-center gap-1 border-b border-slate-100 px-6 py-5 sm:px-10" data-testid="booking-stepper">
              {stepLabels.map((label, i) => {
                const active = i === step;
                const done = i < step;
                return (
                  <div key={label} className="flex flex-1 items-center gap-2 last:flex-none">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                        done ? "bg-emerald-500 text-white" : active ? "bg-azure text-white" : "bg-slate-100 text-navy/40"
                      }`}
                    >
                      {done ? <CheckCircle2 size={15} /> : i + 1}
                    </span>
                    <span className={`hidden text-xs font-semibold sm:block ${active ? "text-navy" : "text-navy/40"}`}>{label}</span>
                    {i < stepLabels.length - 1 && <span className="mx-1 h-px flex-1 bg-slate-200" />}
                  </div>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={`${customMode}-${form.service}-${step}`}
                initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
                className="p-6 sm:p-10"
              >
                {/* -------- ÉTAPE ITINÉRAIRE -------- */}
                {step === 0 && (
                  <div>
                    <h1 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">{t("booking.it_title")}</h1>
                    <p className="mt-1 text-sm text-navy/55">{t("booking.it_sub")}</p>

                    {!devisMode && (
                      <div className="mt-6 grid grid-cols-2 gap-2 rounded-2xl bg-sand p-1.5" data-testid="service-tabs">
                        {[
                          { id: "transfer", label: t("booking.service_transfer") },
                          { id: "hourly", label: t("booking.service_hourly") },
                        ].map((s) => (
                          <button
                            key={s.id} type="button" data-testid={`service-${s.id}-tab`}
                            onClick={() => set("service", s.id)}
                            className={`rounded-xl py-3 text-sm font-bold transition-colors ${
                              form.service === s.id ? "bg-white text-azure shadow-sm" : "text-navy/50 hover:text-navy"
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      {devisMode ? (
                        <>
                          <div>
                            <label className={labelCls}><MapPin size={12} className="mr-1 inline text-azure" />{t("widget.pickup")}</label>
                            <input
                              data-testid="custom-pickup-input" className={fieldCls} placeholder={t("booking.custom_pickup_ph")}
                              value={custom.pickup} onChange={(e) => setCustom((c) => ({ ...c, pickup: e.target.value }))}
                            />
                          </div>
                          <div>
                            <label className={labelCls}><MapPin size={12} className="mr-1 inline text-gold" />{t("widget.dropoff")}</label>
                            <input
                              data-testid="custom-dropoff-input" className={fieldCls} placeholder={t("booking.custom_dropoff_ph")}
                              value={custom.dropoff} onChange={(e) => setCustom((c) => ({ ...c, dropoff: e.target.value }))}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={hourlyMode ? "sm:col-span-1" : ""}>
                            <label className={labelCls}>
                              <MapPin size={12} className="mr-1 inline text-azure" />{t("widget.pickup")}
                              <span className="ml-1 font-bold text-red-500">*</span>
                            </label>
                            <AddressInput
                              testid="pickup-address-input"
                              className={fieldCls}
                              placeholder={t("booking.address_ph")}
                              value={form.pickupAddress}
                              onChange={(v) => { set("pickupAddress", v); setPickupPlace(null); }}
                              onSelect={(p) => { set("pickupAddress", p.label); setPickupPlace(p); }}
                              lang={lang}
                              places={locations}
                            />
                            {pickupPlace && !pickupPlace.exact
                              && quote?.pickup_place?.kind !== "airport" && quote?.pickup_place?.kind !== "station" && (
                              <p className="mt-1.5 text-xs font-medium text-amber-600" data-testid="pickup-exact-hint">{t("booking.exact_hint")}</p>
                            )}
                          </div>
                          {hourlyMode ? (
                            <div>
                              <label className={labelCls}><Clock size={12} className="mr-1 inline text-gold" />{t("booking.hours_label")}</label>
                              <select data-testid="hours-select" value={form.hours} onChange={(e) => set("hours", +e.target.value)} className={fieldCls}>
                                {[2, 3, 4, 5, 6, 7, 8, 10, 12].map((h) => <option key={h} value={h}>{h} h</option>)}
                              </select>
                              <p className="mt-1.5 text-xs font-medium text-azure" data-testid="hourly-hint">{t("booking.hourly_hint")}</p>
                            </div>
                          ) : (
                            <div>
                              <label className={labelCls}>
                                <MapPin size={12} className="mr-1 inline text-gold" />{t("widget.dropoff")}
                                <span className="ml-1 font-bold text-red-500">*</span>
                              </label>
                              <AddressInput
                                testid="dropoff-address-input"
                                className={fieldCls}
                                placeholder={t("booking.address_ph")}
                                value={form.dropoffAddress}
                                onChange={(v) => { set("dropoffAddress", v); setDropoffPlace(null); }}
                                onSelect={(p) => { set("dropoffAddress", p.label); setDropoffPlace(p); }}
                                lang={lang}
                                places={locations}
                              />
                              {dropoffPlace && !dropoffPlace.exact
                                && quote?.dropoff_place?.kind !== "airport" && quote?.dropoff_place?.kind !== "station" && (
                                <p className="mt-1.5 text-xs font-medium text-amber-600" data-testid="dropoff-exact-hint">{t("booking.exact_hint")}</p>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      <div>
                        <label className={labelCls}><Calendar size={12} className="mr-1 inline text-azure" />{t("widget.date")}</label>
                        <input data-testid="date-input" type="date" min={today()} value={form.date} onChange={(e) => set("date", e.target.value)} className={fieldCls} />
                      </div>
                      <div>
                        <label className={labelCls}><Clock size={12} className="mr-1 inline text-azure" />{lang === "en" ? "Pick-up time" : "Heure de prise en charge"}</label>
                        <input data-testid="time-input" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={fieldCls} />
                      </div>
                      <div>
                        <label className={labelCls}><Users size={12} className="mr-1 inline text-azure" />{t("booking.passengers")}</label>
                        <input data-testid="passengers-input" type="number" min={1} max={8} value={form.passengers}
                          onChange={(e) => set("passengers", Math.max(1, Math.min(8, +e.target.value || 1)))} className={fieldCls} />
                      </div>
                      <div>
                        <label className={labelCls}><Luggage size={12} className="mr-1 inline text-azure" />{t("booking.luggage")}</label>
                        <input data-testid="luggage-input" type="number" min={0} max={10} value={form.luggage}
                          onChange={(e) => set("luggage", Math.max(0, Math.min(10, +e.target.value || 0)))} className={fieldCls} />
                      </div>
                      {!customMode && (
                        <div className="sm:col-span-2">
                          <label className={labelCls}>
                            <Plane size={12} className="mr-1 inline text-azure" />
                            {refRequired
                              ? t(pickupKind === "airport" ? "booking.flight_required_airport" : "booking.flight_required_station")
                              : t("booking.flight_label")}
                            {refRequired && <span className="ml-1 font-bold text-red-500">*</span>}
                          </label>
                          <input data-testid="flight-input" placeholder={t("booking.flight_ph")} value={form.flightNumber}
                            onChange={(e) => set("flightNumber", e.target.value)}
                            className={`${fieldCls} ${refRequired ? "border-azure/60 bg-azure-soft/30" : ""}`} />
                          {refRequired && (
                            <p className="mt-1.5 text-xs font-medium text-azure" data-testid="flight-required-hint">
                              {pickupKind === "airport"
                                ? (lang === "en" ? "Your chauffeur tracks the flight and adjusts for delays." : "Votre chauffeur suit le vol et s'ajuste aux retards.")
                                : (lang === "en" ? "Your chauffeur tracks the train and adjusts for delays." : "Votre chauffeur suit le train et s'ajuste aux retards.")}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="sm:col-span-2">
                        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid="child-seat-label">
                          <input data-testid="child-seat-checkbox" type="checkbox" checked={form.childSeat}
                            onChange={(e) => setForm((f) => ({ ...f, childSeat: e.target.checked, childSeatAge: e.target.checked ? f.childSeatAge : "" }))}
                            className="h-4 w-4 rounded border-slate-300 text-azure focus:ring-azure" />
                          <span className="flex items-center gap-2 text-sm font-medium text-navy/70"><Baby size={15} className="text-azure" /> {t("booking.child_seat")}</span>
                        </label>
                        {form.childSeat && (
                          <div className="mt-3" data-testid="child-seat-age-group">
                            <p className="mb-2 text-xs font-semibold text-navy/60">{t("booking.child_seat_age")}</p>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { id: "0-2", tid: "child-seat-age-0-2", label: t("booking.seat_0_2"), sub: t("booking.seat_sub_0_2") },
                                { id: "3-7", tid: "child-seat-age-3-7", label: t("booking.seat_3_7"), sub: t("booking.seat_sub_3_7") },
                                { id: "7+", tid: "child-seat-age-7plus", label: t("booking.seat_7plus"), sub: t("booking.seat_sub_7plus") },
                              ].map((o) => (
                                <button key={o.id} type="button" data-testid={o.tid} onClick={() => set("childSeatAge", o.id)}
                                  className={`rounded-xl border-2 px-3 py-3 text-center transition-colors ${
                                    form.childSeatAge === o.id ? "border-azure bg-azure-soft/50" : "border-slate-200 hover:border-azure/40"
                                  }`}>
                                  <span className="block text-sm font-bold text-navy">{o.label}</span>
                                  <span className="mt-0.5 block text-[11px] leading-tight text-navy/55">{o.sub}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!customMode && loadingQuote && (
                      <p className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold text-azure" data-testid="quote-loading">
                        <Loader2 size={13} className="animate-spin" /> {lang === "en" ? "Calculating your fixed price…" : "Calcul de votre prix fixe…"}
                      </p>
                    )}

                    <button
                      data-testid="continue-itinerary-button" onClick={nextFromItinerary} disabled={!customMode && loadingQuote}
                      className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      {loadingQuote && !customMode ? <Loader2 size={17} className="animate-spin" /> : null}
                      {customMode ? t("common.continue") : t("booking.see_price")}
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                )}

                {/* -------- ÉTAPE VÉHICULE -------- */}
                {!customMode && step === 1 && quote && (
                  <div>
                    <h1 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">{t("booking.veh_title")}</h1>
                    <p className="mt-1 text-sm text-navy/55" data-testid="vehicle-step-route">
                      {hourlyMode
                        ? t("booking.hourly_route", { from: pickupLabel, hours: form.hours })
                        : `${pickupLabel} → ${dropoffLabel} · ${quote.berline.distance_km} km · ~${quote.berline.duration_min} min`}
                    </p>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      {[
                        { id: "berline", name: t("pricing.berline"), cap: t("booking.berline_cap"), q: quote.berline, tag: t("booking.tag_business") },
                        { id: "van", name: lang === "en" ? "Premium Van" : "Van Premium", cap: t("booking.van_cap"), q: quote.van, tag: t("booking.tag_family") },
                      ].map((v) => (
                        <button
                          key={v.id} type="button" data-testid={`vehicle-${v.id}-card`}
                          onClick={() => set("vehicle", v.id)}
                          className={`rounded-3xl border-2 p-7 text-left transition-[border-color,box-shadow,transform] hover:-translate-y-1 ${
                            form.vehicle === v.id
                              ? "border-azure bg-azure-soft/50 shadow-lg shadow-azure/10"
                              : "border-slate-200 bg-white hover:border-azure/40"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <Car size={26} className={form.vehicle === v.id ? "text-azure" : "text-navy/40"} />
                            {form.vehicle === v.id && <CheckCircle2 size={22} className="text-azure" />}
                          </div>
                          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-navy/45">{v.tag}</p>
                          <h3 className="mt-1 font-serif text-xl font-semibold text-navy">{v.name}</h3>
                          <p className="mt-1 text-xs text-navy/55">{v.cap}</p>
                          <p className="mt-4 font-serif text-4xl font-semibold text-navy" data-testid={`vehicle-${v.id}-price`}>
                            {v.q.price_eur} <span className="text-xl">€</span>
                          </p>
                          <p className="text-xs text-navy/45">{t("booking.all_included")}</p>
                        </button>
                      ))}
                    </div>

                    <div className="mt-8 flex gap-3">
                      <button data-testid="back-to-itinerary-button" onClick={() => setStep(0)}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-slate-50">
                        <ArrowLeft size={15} /> {t("common.back")}
                      </button>
                      <button data-testid="continue-vehicle-button" onClick={() => setStep(2)}
                        className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5">
                        {t("common.continue")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  </div>
                )}

                {/* -------- ÉTAPE COORDONNÉES -------- */}
                {((!customMode && step === 2) || (customMode && step === 1)) && (
                  <div>
                    <h1 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">{t("booking.contact_title")}</h1>
                    <p className="mt-1 text-sm text-navy/55">{t("booking.contact_sub")}</p>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className={labelCls}><User size={12} className="mr-1 inline text-azure" />{t("booking.name_label")}</label>
                        <input data-testid="name-input" placeholder={t("booking.name_ph")} value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Email</label>
                        <input data-testid="email-input" type="email" placeholder="vous@exemple.com" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} />
                      </div>
                      <div>
                        <label className={labelCls}>{t("booking.phone_label")}</label>
                        <input data-testid="phone-input" type="tel" placeholder="+33 6 ..." value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>{t("booking.notes_label")}</label>
                        <textarea data-testid="notes-input" rows={3} placeholder={t("booking.notes_ph")}
                          value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${fieldCls} resize-none`} />
                      </div>
                    </div>

                    <div className="mt-8 flex gap-3">
                      <button data-testid="back-from-contact-button" onClick={() => setStep(customMode ? 0 : 1)}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-slate-50">
                        <ArrowLeft size={15} /> {t("common.back")}
                      </button>
                      {customMode ? (
                        <button data-testid="submit-devis-button" onClick={submitDevis} disabled={submitting}
                          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-colors hover:bg-azure-hover disabled:opacity-60">
                          {submitting && <Loader2 size={16} className="animate-spin" />} {t("booking.send_quote")}
                        </button>
                      ) : (
                        <button data-testid="continue-contact-button" onClick={() => setStep(3)}
                          className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-[background-color,transform] hover:bg-azure-hover hover:-translate-y-0.5">
                          {t("booking.choose_payment")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* -------- ÉTAPE PAIEMENT -------- */}
                {!customMode && step === 3 && quote && (
                  <div>
                    <h1 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">{t("booking.pay_title")}</h1>
                    <p className="mt-1 text-sm text-navy/55">{t("booking.pay_sub")}</p>

                    <div className="mt-8 grid gap-4 sm:grid-cols-3">
                      {PAY_OPTIONS.map((p) => (
                        <button key={p.id} type="button" data-testid={`payment-${p.id}-radio`} onClick={() => set("paymentMethod", p.id)}
                          className={`rounded-2xl border-2 p-5 text-left transition-colors ${form.paymentMethod === p.id ? "border-azure bg-azure-soft/50" : "border-slate-200 hover:border-azure/40"}`}>
                          <p.icon size={22} className="text-azure" />
                          <p className="mt-3 text-sm font-bold text-navy">{p.title}</p>
                          <p className="mt-1 text-xs leading-relaxed text-navy/55">{p.text}</p>
                        </button>
                      ))}
                    </div>

                    <div className="mt-8 rounded-2xl bg-sand p-6" data-testid="booking-summary">
                      <p className="overline-label mb-4 !text-navy/40">{t("booking.summary")}</p>
                      <dl className="space-y-2.5 text-sm">
                        <div className="flex justify-between"><dt className="text-navy/50">{t("booking.s_route")}</dt><dd className="text-right font-semibold text-navy">{hourlyMode ? t("booking.hourly_route", { from: pickupLabel, hours: form.hours }) : `${pickupLabel} → ${dropoffLabel}`}</dd></div>
                        <div className="flex justify-between"><dt className="text-navy/50">{t("booking.s_datetime")}</dt><dd className="font-semibold text-navy">{form.date} {t("booking.s_at")} {form.time}</dd></div>
                        <div className="flex justify-between"><dt className="text-navy/50">{t("booking.s_vehicle")}</dt><dd className="font-semibold text-navy">{form.vehicle === "berline" ? t("pricing.berline") : (lang === "en" ? "Premium Van" : "Van Premium")}</dd></div>
                        <div className="flex justify-between"><dt className="text-navy/50">{t("booking.s_passengers")}</dt><dd className="font-semibold text-navy">{form.passengers} · {form.luggage} {t("booking.s_luggage")}</dd></div>
                        <div className="flex justify-between border-t border-slate-200 pt-3">
                          <dt className="font-bold text-navy">{t("booking.s_total")}</dt>
                          <dd className="font-serif text-2xl font-semibold text-azure" data-testid="summary-price">
                            {(form.vehicle === "berline" ? quote.berline : quote.van).price_eur} €
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <p className="mt-4 flex items-center gap-2 text-xs text-navy/50">
                      <ShieldCheck size={14} className="text-emerald-500" /> {t("booking.cancel_note")}
                    </p>

                    <div className="mt-6 flex gap-3">
                      <button data-testid="back-from-payment-button" onClick={() => setStep(2)}
                        className="flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-slate-50">
                        <ArrowLeft size={15} /> {t("common.back")}
                      </button>
                      <button data-testid="submit-booking-button" onClick={submitBooking} disabled={submitting}
                        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-azure py-4 text-sm font-bold text-white shadow-lg shadow-azure/25 transition-colors hover:bg-azure-hover disabled:opacity-60">
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {form.paymentMethod === "online" ? t("booking.pay_confirm") : t("booking.confirm_booking")}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-navy/40">
          <Logo /> <span>— Chauffeur privé Côte d'Azur</span>
        </div>
      </main>

      <Footer />
    </div>
  );
}
